import { type FC } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Button, Icon, Switch, Typography } from 'shared/ui'
import { numberFormat } from 'shared/lib'
import { type AutoRechargeWidgetStore } from 'entities/Billing/ui/AutoRechargeModal'
import styles from './styles.module.scss'

type IAutoRechargeCardProps = {
  className?: string
  version?: 'standard' | 'borderWithIcon' | 'borderWithIconSmall'
  showFreeLabel?: boolean
  store: AutoRechargeWidgetStore
  onSwitchChange?: (value: boolean) => boolean | void
  onEditThresholdClick?: () => boolean | void
}

export const AutoRechargeCard: FC<IAutoRechargeCardProps> = observer(
  ({
    store,
    className,
    version = 'standard',
    showFreeLabel = false,
    onSwitchChange,
    onEditThresholdClick,
  }) => {
    const {
      autorecharge,
      isActive,
      handleSwitchChange,
      openModal,
      thresholdInCredits,
      amountInCredits,
      price,
    } = store

    const handleSwitch = (value: boolean) => {
      if (onSwitchChange?.(value) === true) return
      handleSwitchChange(value)
    }

    const handleEditThreshold = () => {
      if (onEditThresholdClick?.() === true) return
      openModal()
    }

    if (!autorecharge) return null

    const credit = numberFormat({ value: amountInCredits ?? 0 })
    const priceValue = numberFormat({ value: price ?? 0, minimumFractionDigits: 2 })
    const threshold = numberFormat({ value: thresholdInCredits ?? 0 })

    const isBorderWithIcon = version === 'borderWithIcon'
    const isBorderWithIconSmall = version === 'borderWithIconSmall'
    const hasIconAndBorder = isBorderWithIcon || isBorderWithIconSmall

    const iconColor =
      isBorderWithIconSmall || isActive
        ? 'var(--content-brand-primary)'
        : 'var(--content-primary-secondary)'
    const iconSize = isBorderWithIconSmall ? 16 : 26
    const titleVariant = isBorderWithIconSmall ? 'body-md-semibold' : 'body-lg-medium'
    const switchSize = isBorderWithIconSmall ? 'small' : 'middle'

    return (
      <div
        className={classNames(styles.wrap, className, {
          [styles.wrapBorderWithIcon]: hasIconAndBorder,
          [styles.wrapActive]: isBorderWithIcon && isActive,
        })}
      >
        {hasIconAndBorder && <Icon icon='ovalRefresh' fontSize={iconSize} color={iconColor} />}
        <div className={styles.content}>
          <Typography
            ariaLabel='auto_recharge_title'
            variant={titleVariant}
            color='var(--content-primary-primary)'
          >
            Auto-Recharge: Guarantee Delivery
          </Typography>
          {showFreeLabel && (
            <Typography
              ariaLabel='auto_recharge_free_label'
              variant='body-md-regular'
              color='var(--green-80)'
              className={styles.freeLabel}
            >
              FREE
            </Typography>
          )}
          <Typography
            ariaLabel='auto_recharge_desc'
            variant='body-md-regular'
            color='var(--content-primary-tertiary)'
            className={styles.description}
          >
            {`Your credits will be auto-recharged to ${credit} credits ($${priceValue}) if the number of credits drops below ${threshold}.`}
          </Typography>
        </div>
        <div
          className={classNames(styles.switchContainer, {
            [styles.switchContainerHorizontal]: !hasIconAndBorder,
          })}
        >
          <Switch
            size={switchSize}
            value={isActive}
            onChange={handleSwitch}
            className={hasIconAndBorder ? '' : styles.switch}
            ariaLabel='auto_recharge_switch'
          />
          <Button
            text='Edit threshold'
            typeBtn='text'
            size='medium'
            color='var(--light-blue-60)'
            onClick={handleEditThreshold}
          />
        </div>
      </div>
    )
  }
)
